/**
 * @link https://github.com/axios/axios
 */
import axios from 'axios'
import { clone, compose, has, inc, isEmpty, merge, not, promiseTry, prop, propOr, toString, typeOf } from '~/utils/lib/core/prelude'
import { baseURL, ERRORS } from '~/utils/infra/hamac/types'
import httpInterceptor from '~/utils/infra/hamac/interceptor'

/** @type {import('./types').RequestConfig} */
export const AXIOS_CONFIG = {
  baseURL,
  timeout: 0, // no timeout handling
  validateStatus: status => status >= 200 && status < 300,
  responseType: 'json',
  responseEncoding: 'utf8',
  headers: {},
  // custom properties
  withAuthorization: false,
  execCount: 0,
}
/**
 * @constructor
 * @param {import('./types').RequestConstructorParams} _
 * @throws {Error}
 */
const Request = function({
  method,
  route,
  data = undefined,
  params = undefined,
}) {
  // @link https://axios-http.com/docs/req_config
  /** @type {import('./types').RequestConfig} */
  this.config = {
    method,
    data,
    params,
    url: route,
    ...AXIOS_CONFIG
  }

  /** @type {import('axios').AxiosInstance} */
  // @ts-ignore: UNSAFE coerce (lazy pattern)
  this.handler = undefined

  /** @type {import('./types').RequestResponse} */
  // @ts-ignore: UNSAFE coerce (lazy pattern)
  this.response = undefined

  /** @type {import('./error').default} */
  // @ts-ignore: UNSAFE coerce (lazy pattern)
  this.error = undefined

  initialize.call(this)
}

export default Request
/**
 * @function initialize
 * @this {Request}
 * @throws {Error}
 */
function initialize() {
  [
    setHandler,
    addInterceptor,
  ].forEach(init => init.call(this))
}
/**
 * @function accept
 * @param {*} visitor
 * @returns {Promise<void>}
 */
Request.prototype.accept = function(visitor) {
  return promiseTry(() => visitor.visit(this))
}
/**
 *  @function exec
 *  @constructs Request.prototype#
 *  @rejects {import('./error').default}
 *  @return {Promise<import('./types').RequestResponse["data"]>}
 */
Request.prototype.exec = function() {
  this.config.execCount = inc(this.config.execCount)

  // @XXX Axios instance config and `.request` method
  //      Contrary to documentation, below methods needs to provide again the
  //      instance config (regression from previous Axios release)
  return this.handler.request( this.config )
}
/**
 * @function authorize
 * @constructs Request.prototype#
 * @return {Request} chainable build
 */
Request.prototype.authorize = function() {
  this.config.withAuthorization = true
  this.config.withCredentials = true

  return this
}
/**
 * @function setHeaders
 * @constructs Request.prototype#
 * @param {object} headers
 * @return {Request} chainable build
 */
Request.prototype.setHeaders = function(headers) {
  this.config.headers = merge(this.config.headers || {}, headers)

  return this
}
/**
 * @function setResponseType
 * @constructs Request.prototype#
 * @param {import('axios').ResponseType} responseType
 * @return {Request} chainable build
 */
Request.prototype.setResponseType = function(responseType) {
  this.config.responseType = responseType

  return this
}
/**
 * @function setHandler
 * @this {Request}
 */
function setHandler() {
  this.handler = axios.create(this.config)
}
/**
 *  @XXX Axios and multiple interceptors
 *       Not mentioned in their documents, current Axios release cannot bear
 *       multiple interceptors (only one `.use` can be made)
 *
 *  @link https://axios-http.com/docs/interceptors
 *
 *  @function addInterceptor
 *  @this {Request}
 */
function addInterceptor() {
  this.handler.interceptors.response.use(
    // @ts-ignore: UNSAFE coerce
    (...args) => httpInterceptor.response.apply(this, args),
    // @ts-ignore: UNSAFE coerce
    (...args) => httpInterceptor.responseError.apply(this, args)
  )
}
/**
 * @function requestResponsePrism
 * @param {import('~/utils/infra/account/types').RequestError} error
 * @throws {Error}
 * @returns {import('~/utils/infra/account/types').RequestResponse}
 */
export function requestResponsePrism(error) {
  const response = propOr(undefined, 'response', error)

  switch (typeOf(response)) {

    case 'object':
      // @ts-ignore: (!) PARTIAL, assuming a matched <object> leads to a
      //                 proper <RequestResponse>
      return clone(response)

    default:
      throw new Error(ERRORS.REQUEST_RESPONSE_UNKNOWN_SCHEMA)
  }
}
/**
 * @function dataPrism
 * @param {import('~/utils/infra/hamac/types').RequestResponse} response
 * @throws {Error}
 * @return {any}
 */
export function dataPrism(response) {
  const data = propOr(undefined, 'data', response)

  switch (compose(not, isEmpty)(data)) {

    case true:  return clone(data)
    case false: throw new Error(ERRORS.REQUEST_RESPONSE_UNKNOWN_DATA)
  }
}
/**
 * @function messagePrism
 * @desc extracting error informations from <RequestResponse.data>
 *
 *    a) Micro service readable error
 *        {
 *          err:
 *            {
 *              message: "some error occurred"
 *            }
 *        }
 *
 *    b) Micro service error name fallback
 *        {
 *          err:
 *            {
 *              name: "ErrorName"
 *            }
 *        }
 *
 *    c) Micro service error shortcut (1)
 *        {
 *          err: "some error occurred"
 *        }
 *
 *    d) Micro service error shortcut (2)
 *        "some error occurred"
 *
 *    e) no error provided
 *
 * @param {import('~/utils/infra/hamac/types').RequestResponse} response
 * @throws {Error}
 * @return {string}
 */
export function messagePrism(response) {
  const data = dataPrism(response)
  const /** @type {any} */ err = propOr(undefined, 'err', data)

  try {

    if (typeOf(err) !== 'object')
      throw `not containing <err> object`

    // a) Micro service readable error
    if (has('message', err))
      return compose(toString, prop('message'))(err)

    // b) Micro service error name fallback
    if (has('name', err))
      return compose(toString, prop('name'))(err)

    throw `unrecognised <err> pattern matching`

  } catch (_) {

    // c) Micro service error shortcut (1)
    if (typeOf(err) === 'string')
      return err

    // d) Micro service error shortcut (2)
    if (typeOf(data) === 'string')
      return data

    // e) no error provided
    throw new Error(ERRORS.REQUEST_RESPONSE_UNKNOWN_MESSAGE)
  }
}
/**
 * @function causePrism
 * @desc extracting error informations from <RequestResponse.data>
 *
 *    a) Micro service error name is provided
 *        {
 *          err:
 *            {
 *              name: "ErrorName"
 *            }
 *        }
 *
 *    b) no error name has been EXPLICITLY provided
 *
 * @param {import('~/utils/infra/hamac/types').RequestResponse} response
 * @throws {Error}
 * @return {string}
 */
export function causePrism(response) {
  const data = dataPrism(response)
  const err = propOr(undefined, 'err', data)

  try {

    if (typeOf(err) !== 'object')
      throw `not containing <err> object`

    // a) Micro service error name is provided
    if (has('name', err))
      return compose(toString, prop('name'))(err)

    throw `unrecognised <err> pattern matching`

  } catch (_) {

    // b) no error name has been EXPLICITLY provided
    throw new Error(ERRORS.REQUEST_RESPONSE_UNKNOWN_CAUSE)
  }
}
